import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useScopeParams } from "./useScopeParams";
import { documentWorkspaceService } from "@/services/documentWorkspaceService";
import { DOCUMENT_WORKSPACE_DATASET_TYPE } from "@/views/DocumentWorkspace/documentWorkspaceMapping";
import type {
  NewVersionPayload,
  RoleAccessPayload,
  UploadDocumentPayload,
} from "@/views/DocumentWorkspace/documentWorkspace.types";

export function useDocumentWorkspaceMutations() {
  const { id } = useScopeParams();
  const queryClient = useQueryClient();

  const invalidateDocuments = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const [keyPrefix, , body] = query.queryKey as [string, string, unknown];
        if (
          typeof keyPrefix === "string" &&
          keyPrefix.includes(DOCUMENT_WORKSPACE_DATASET_TYPE)
        ) {
          return true;
        }
        return (
          !!body &&
          typeof body === "object" &&
          (body as { dataset_type?: string }).dataset_type ===
            DOCUMENT_WORKSPACE_DATASET_TYPE
        );
      },
    });
  }, [queryClient]);

  const uploadDocumentMutation = useMutation({
    mutationFn: (payload: UploadDocumentPayload) =>
      documentWorkspaceService.uploadDocument(id, payload),
    onSuccess: invalidateDocuments,
  });

  const addVersionMutation = useMutation({
    mutationFn: ({
      documentId,
      payload,
    }: {
      documentId: string;
      payload: NewVersionPayload;
    }) => documentWorkspaceService.addVersion(id, documentId, payload),
    onSuccess: invalidateDocuments,
  });

  const setRoleAccessMutation = useMutation({
    mutationFn: ({
      documentId,
      payload,
    }: {
      documentId: string;
      payload: RoleAccessPayload;
    }) => documentWorkspaceService.setRoleAccess(id, documentId, payload),
    onSuccess: invalidateDocuments,
  });

  return {
    uploadDocument: uploadDocumentMutation.mutateAsync,
    addVersion: addVersionMutation.mutateAsync,
    setRoleAccess: setRoleAccessMutation.mutateAsync,
    isUploading: uploadDocumentMutation.isPending,
    isAddingVersion: addVersionMutation.isPending,
    isSettingRoleAccess: setRoleAccessMutation.isPending,
  };
}
